'use client';
import { Button, ControlledInput, Typography } from 'components';
import { THEME_TRANSLATION_REQUEST_STATUS_FILTER } from 'constants/status';
import { CommentsFilterEnum, TranslationRequestStatusFilterEnum } from 'gql/graphql';
import useOptionsAgents from 'hooks/useOptionsAgents';
import useOptionsClients from 'hooks/useOptionsClients';
import useOptionsPairLanguages from 'hooks/useOptionsPairLanguages';
import useOptionsQuality from 'hooks/useOptionsQuality';
import { CloseIcon, SearchIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { useFeatureFlags } from 'providers/ClientProviders/useFeatureFlags';
import { FC } from 'react';
import { Control } from 'react-hook-form';
import { KeysEnum } from 'types/KeysEnum';
import { FiltersTranslationForm } from '../page';

interface IFiltersTranslation {
  control: Control<FiltersTranslationForm>;
  onClear?: () => void;
  filtersAdded?: boolean;
}

const FiltersTranslation: FC<IFiltersTranslation> = ({ control, filtersAdded, onClear }) => {
  const t = useTranslations();

  const { optionsClients } = useOptionsClients();
  const { optionsQuality } = useOptionsQuality();
  const { optionsLanguages } = useOptionsPairLanguages();
  const { optionsAgents } = useOptionsAgents({ active: true });
  const { data: featureFlags } = useFeatureFlags();

  const optionsComments = [
    { label: t('translation.index.comments.any'), value: undefined },
    {
      label: t(`translation.index.comments.${CommentsFilterEnum.With}`),
      value: CommentsFilterEnum.With
    },
    {
      label: t(`translation.index.comments.${CommentsFilterEnum.Without}`),
      value: CommentsFilterEnum.Without
    },

    {
      label: t(`translation.index.comments.${CommentsFilterEnum.Pending}`),
      value: CommentsFilterEnum.Pending
    }
  ];

  const checkboxes = [
    {
      label: t(`sources.${TranslationRequestStatusFilterEnum.Unordered}`),
      icon: (
        <span className="scale-[0.8]">
          {
            THEME_TRANSLATION_REQUEST_STATUS_FILTER[TranslationRequestStatusFilterEnum.Unordered]
              ?.icon
          }
        </span>
      ),
      value: TranslationRequestStatusFilterEnum.Unordered
    },
    {
      label: t(`sources.${TranslationRequestStatusFilterEnum.InProgress}`),
      icon: (
        <span className="scale-[0.8]">
          {
            THEME_TRANSLATION_REQUEST_STATUS_FILTER[TranslationRequestStatusFilterEnum.InProgress]
              ?.icon
          }
        </span>
      ),
      value: TranslationRequestStatusFilterEnum.InProgress
    },
    {
      label: t(`sources.${TranslationRequestStatusFilterEnum.ReadyToReview}`),
      icon: (
        <span className="scale-[0.8]">
          {
            THEME_TRANSLATION_REQUEST_STATUS_FILTER[
              TranslationRequestStatusFilterEnum.ReadyToReview
            ]?.icon
          }
        </span>
      ),
      value: TranslationRequestStatusFilterEnum.ReadyToReview
    },
    {
      label: t(`sources.${TranslationRequestStatusFilterEnum.Finished}`),
      icon: (
        <span className="scale-[0.8]">
          {
            THEME_TRANSLATION_REQUEST_STATUS_FILTER[TranslationRequestStatusFilterEnum.Finished]
              ?.icon
          }
        </span>
      ),
      value: TranslationRequestStatusFilterEnum.Finished
    },
    {
      label: t(`sources.${TranslationRequestStatusFilterEnum.Failed}`),
      icon: (
        <span className="scale-[0.8]">
          {THEME_TRANSLATION_REQUEST_STATUS_FILTER[TranslationRequestStatusFilterEnum.Failed]?.icon}
        </span>
      ),
      value: TranslationRequestStatusFilterEnum.Failed
    }
  ];

  return (
    <div className="relative z-30 mb-10 w-full">
      <div className="mb-3 flex items-center justify-between">
        <Typography variant="medium1" className={'text-tertiary-100 '}>
          {t('translation.filters_translation.filters')}
        </Typography>
        {filtersAdded && (
          <Button
            size="small"
            variant="text"
            color="secondary"
            label={t('translation.filters_translation.clear_all')}
            className="!m-0 !h-7 w-fit !rounded-full !px-2 !shadow-none"
            classNameTypography="text-xs !m-0"
            endAdornment={<CloseIcon className="scale-[0.7]" />}
            onClick={onClear}
          />
        )}
      </div>
      <ControlledInput
        control={control}
        label={t('translation.filters_translation.label_search')}
        hint={t('translation.filters_translation.hint_search')}
        type="text"
        name={KeysEnum.Query}
        placeholder={t('translation.filters_translation.place_holder')}
        classNameParent="!w-full mt-4"
        endAdornment={<SearchIcon />}
      />
      <ControlledInput
        control={control}
        label={t('translation.filters_translation.label_language_pair')}
        type="select"
        name={KeysEnum.Languages}
        classNameParent="pt-3"
        options={optionsLanguages}
      />
      <ControlledInput
        control={control}
        type="date-range"
        name={KeysEnum.DateRange}
        label={t('translation.filters_translation.label_date_range')}
        classNameParent="pt-3"
      />
      <ControlledInput
        control={control}
        type="date-range"
        name={KeysEnum.DatePreferred}
        label={t('translation.filters_translation.label_preferred_date')}
        classNameParent="pt-3"
      />
      <ControlledInput
        control={control}
        label={t('translation.filters_translation.label_quality')}
        type="select"
        name={KeysEnum.Quality}
        classNameParent="pt-3"
        options={optionsQuality}
      />
      {optionsClients?.length > 2 && (
        <ControlledInput
          control={control}
          label={t('translation.filters_translation.label_created_by_user')}
          type="select"
          name={KeysEnum.EditorId}
          classNameParent="pt-3"
          options={optionsClients}
        />
      )}
      <ControlledInput
        control={control}
        label={t('translation.filters_translation.label_comments')}
        type="select"
        name={KeysEnum.Comments}
        classNameParent="pt-3"
        options={optionsComments}
      />
      {/* Agents */}
      {featureFlags?.agentsEnabled && (
        <ControlledInput
          control={control}
          label={t('translation.filters_translation.label_agent')}
          placeholder={t('placeholders.select_agent')}
          type="select"
          name={KeysEnum.WorkflowId}
          classNameParent="pt-3"
          options={optionsAgents}
        />
      )}
      <ControlledInput
        control={control}
        label={t('translation.filters_translation.label_duplicated_translation')}
        type="checkbox"
        name={KeysEnum.ShowDuplicates}
        classNameParent="pt-3"
      />
      {featureFlags?.agentsEnabled && (
        <ControlledInput
          control={control}
          label={t('translation.filters_translation.label_all_agent_workflow')}
          type="checkbox"
          name={KeysEnum.TranslatedByAgent}
          classNameParent="pt-3"
        />
      )}

      <ControlledInput
        control={control}
        label={t('translation.filters_translation.filter_by')}
        type="filter"
        name={KeysEnum.Status}
        classNameParent="pt-3"
        checkboxes={checkboxes}
      />
    </div>
  );
};
export default FiltersTranslation;
